import { MoreHorizontal, Pencil, Trash2, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { Dashboard } from '../../types/analytics.types';
import { cn } from '@/lib/utils';

interface DashboardListItemProps {
  dashboard: Dashboard;
  isActive: boolean;
  onClick: () => void;
  onRename: () => void;
  onDelete: () => void;
  onSetDefault: () => void;
}

export function DashboardListItem({
  dashboard,
  isActive,
  onClick,
  onRename,
  onDelete,
  onSetDefault,
}: DashboardListItemProps) {
  return (
    <div
      className={cn(
        'group flex items-center justify-between rounded-md px-3 py-2 cursor-pointer transition-colors',
        isActive
          ? 'bg-primary text-primary-foreground'
          : 'hover:bg-muted text-foreground'
      )}
      onClick={onClick}
    >
      <div className="flex items-center gap-2 min-w-0">
        {dashboard.isDefault && (
          <Star className="h-3 w-3 flex-shrink-0 fill-current" />
        )}
        <span className="truncate text-sm font-medium">{dashboard.name}</span>
        <span
          className={cn(
            'text-xs',
            isActive ? 'text-primary-foreground/70' : 'text-muted-foreground'
          )}
        >
          ({dashboard.cards?.length || 0})
        </span>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              'h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity',
              isActive && 'hover:bg-primary-foreground/10'
            )}
          >
            <MoreHorizontal className="h-4 w-4" />
            <span className="sr-only">Dashboard options</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={onRename}>
            <Pencil className="mr-2 h-4 w-4" />
            Rename
          </DropdownMenuItem>
          {!dashboard.isDefault && (
            <DropdownMenuItem onClick={onSetDefault}>
              <Star className="mr-2 h-4 w-4" />
              Set as default
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onClick={onDelete}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
